import { Link, router } from "@inertiajs/react"
import { ArrowRightLeft, BellRing, Clock3, Pin, Send, StickyNote } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { TimelineEvent } from "@/types/leadflow"

export function ActivityTimeline({ events, showLeadLinks = false }: { events: TimelineEvent[]; showLeadLinks?: boolean }) {
  if (events.length === 0) return <div className="callout mt-4"><p>No activity yet.</p></div>
  return <ol className="mt-4 space-y-3">{events.map((event) => {
    const icon = iconFor(event.type)
    return <li key={event.id} className="rounded-md border border-hairline bg-page p-4">
      <div className="flex items-start gap-3">
        {icon}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><strong>{event.title}</strong>{event.pinned && <Badge tone="signal"><Pin className="h-3 w-3" /> Important</Badge>}{event.type === "message" && <Badge tone={event.status === "blocked" ? "signal" : "accent"}>{event.status === "blocked" ? "Blocked" : "Message"}</Badge>}{event.type === "follow_up" && <Badge tone={event.state === "completed" ? "accent" : event.state === "blocked" ? "signal" : "muted"}>{event.state || "Follow-up"}</Badge>}{event.type === "webhook" && <Badge tone={event.result_status === "error" || event.result_status === "rejected" ? "signal" : "neutral"}>FieldRoutes</Badge>}</div>
          <p className="mt-1 whitespace-pre-wrap">{event.detail}</p>
          <p className="mt-2 text-xs text-ink-muted">{event.actor} · {formatDate(event.occurred_at)}{showLeadLinks && event.lead_id ? <> · <Link href={`/dashboard?lead_id=${event.lead_id}`}>View lead</Link></> : null}</p>
        </div>
        {event.type === "note" && event.note_id && <Button type="button" variant="ghost" size="sm" onClick={() => router.patch(`/activity_notes/${event.note_id}`, { activity_note: { pinned: !event.pinned } }, { preserveScroll: true })}>{event.pinned ? "Unpin" : "Pin"}</Button>}
      </div>
    </li>
  })}</ol>
}

function iconFor(type: TimelineEvent["type"]) {
  const className = "mt-1 h-4 w-4"
  switch (type) {
    case "note":
      return <StickyNote className={`${className} text-accent`} />
    case "message":
      return <Send className={`${className} text-accent`} />
    case "follow_up":
      return <BellRing className={`${className} text-ink-muted`} />
    case "import":
    case "webhook":
      return <ArrowRightLeft className={`${className} text-ink-muted`} />
    default:
      return <Clock3 className={`${className} text-ink-muted`} />
  }
}

export function formatDate(value: string | null) {
  if (!value) return "Not recorded"
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
}

export function relativeTime(value: string | null) {
  if (!value) return "Never"
  const hours = Math.round((Date.now() - new Date(value).getTime()) / 3_600_000)
  if (hours < 24) return `${Math.max(hours, 0)}h ago`
  return `${Math.round(hours / 24)}d ago`
}
